import { Suspense, useEffect, useState, type ReactNode } from "react";
import { createInstance, type i18n } from "i18next";
import { I18nextProvider, useTranslation } from "react-i18next";
import type { UiLocale } from "@tubo/api-models/business/common/ui-locale";
import { uiCoreResources } from "./core-resources.js";
import { uiResourceBackend } from "./namespace-resource-loader.js";
import type { UiNamespace } from "./resources.js";

export function createUiI18n(locale: UiLocale): i18n {
  const instance = createInstance();
  void instance.use(uiResourceBackend).init({
    lng: locale,
    fallbackLng: "zh-CN",
    supportedLngs: ["zh-CN", "en-US"],
    defaultNS: "common",
    ns: Object.keys(uiCoreResources["zh-CN"]),
    resources: {
      "zh-CN": uiCoreResources["zh-CN"],
      "en-US": uiCoreResources["en-US"],
    },
    partialBundledLanguages: true,
    initAsync: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: true },
    saveMissing: import.meta.env?.DEV,
    missingKeyHandler: import.meta.env?.DEV
      ? (_languages, namespace, key) =>
          console.warn(`[i18n] Missing ${namespace}:${key}`)
      : undefined,
  });
  return instance;
}

export function UiI18nProvider({
  locale,
  children,
  documentTitleKey,
}: {
  locale: UiLocale;
  children: ReactNode;
  documentTitleKey?: string;
}) {
  const [instance] = useState(() => createUiI18n(locale));
  useEffect(() => {
    let active = true;
    void instance.changeLanguage(locale).then(() => {
      if (!active) return;
      document.documentElement.lang = locale;
      if (documentTitleKey) document.title = instance.t(documentTitleKey);
    });
    return () => {
      active = false;
    };
  }, [documentTitleKey, instance, locale]);
  return (
    <I18nextProvider i18n={instance}>
      <Suspense
        fallback={
          <div role="status">{locale === "en-US" ? "Loading…" : "加载中…"}</div>
        }
      >
        {children}
      </Suspense>
    </I18nextProvider>
  );
}

export function useUiTranslation(namespace: UiNamespace = "common") {
  return useTranslation(namespace);
}

export function formatUiNumber(
  value: number,
  locale: string,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}

export { uiCoreResources };
